import React, { memo } from 'react';
import { useSelector } from 'react-redux';

import { useFeatureToggle } from '@flopflip/react-redux';
import { AppBar, Box, IconButton } from '@mui/material';
import LenderLogo from 'app/shared/componentsRestyled/common/LenderLogo';
import { CancelIcon, MenuIcon } from 'app/shared/componentsRestyled/coreDesignSystem/icons';
import PropTypes from 'prop-types';

import { COMPOSED_CLAIMS } from 'constants/composedClaims';
import { IconSize } from 'constants/coreDesignSystem';
import { FeatureFlag } from 'constants/featureFlags';
import { PAGE_CODES } from 'constants/pageCodes';
import { IS_SWITCH_THEME_MODE_ALLOWED } from 'constants/themes';
import { COLOR_PALETTE } from 'constants/ui';

import {
  selectIsClaimAllowed,
  selectMasterBranding,
  selectMasterCompany,
} from 'store/auth/selectors';
import { selectCurrentPageCode, selectIsDesktop } from 'store/global/selectors';

import { headerActionsStyles, headerStyles, iconButtonStyles } from './Header.styles';
import { HeaderTestIds } from './Header.testIds';
import HeaderChatBell from './HeaderChatBell';
import HelpButton from './HelpButton';
import MasterMenu from './MasterMenu';
import NavigationMenu from './NavigationMenu';
import PushNotificationBell from './PushNotificationBell';
import TelephonyWidget from './TelephonyWidget';
import ThemeToggle from './ThemeToggle';

const PAGE_CODES_WITHOUT_NAVIGATION = [PAGE_CODES.FILE_REQUEST];

const Header = ({ windowWidth, isSidebarOpen = false, toggleIsSidebarOpen }) => {
  const currentPageCode = useSelector(selectCurrentPageCode);
  const isDesktop = useSelector(selectIsDesktop);
  const branding = useSelector(selectMasterBranding);
  const company = useSelector(selectMasterCompany);

  const isTelephonyWidgetEnabled = useSelector(
    selectIsClaimAllowed(COMPOSED_CLAIMS.TELEPHONY_ENABLED),
  );
  const isNotificationCenterClaimAllowed = useSelector(
    selectIsClaimAllowed(COMPOSED_CLAIMS.NOTIFICATION_CENTER_ACCESS),
  );

  const isNotificationCenterFeatureEnabled = useFeatureToggle(FeatureFlag.EnableNotificationCenter);

  const isNotificationCenterEnabled =
    isNotificationCenterClaimAllowed && isNotificationCenterFeatureEnabled;

  const isNavigable = !PAGE_CODES_WITHOUT_NAVIGATION.includes(currentPageCode);

  return (
    <AppBar id="header" sx={headerStyles}>
      {!isDesktop && isNavigable && toggleIsSidebarOpen && (
        <IconButton
          disableRipple
          data-testid={HeaderTestIds.SIDEBAR_TOGGLE}
          sx={iconButtonStyles}
          onClick={toggleIsSidebarOpen}
        >
          {isSidebarOpen ? (
            <CancelIcon color={COLOR_PALETTE.GRAY[7]} size={IconSize[20]} />
          ) : (
            <MenuIcon size={IconSize[20]} />
          )}
        </IconButton>
      )}

      <LenderLogo alt={company?.name} isClickable={isNavigable} logoUrl={branding?.logoUrl} />

      {isDesktop && isNavigable && <NavigationMenu windowWidth={windowWidth} />}

      <Box sx={headerActionsStyles}>
        {isNavigable && <HeaderChatBell />}

        {isNavigable && isTelephonyWidgetEnabled && <TelephonyWidget />}

        {isNotificationCenterEnabled && <PushNotificationBell />}

        {isNavigable && <HelpButton />}

        {isDesktop && isNavigable && IS_SWITCH_THEME_MODE_ALLOWED && <ThemeToggle />}

        {isDesktop && isNavigable && <MasterMenu />}
      </Box>
    </AppBar>
  );
};

Header.propTypes = {
  windowWidth: PropTypes.number.isRequired,
  isSidebarOpen: PropTypes.bool,
  toggleIsSidebarOpen: PropTypes.func,
};

export default memo(Header);
